import React, { useEffect, useMemo, useState } from "react";
import {
  MdCheck,
  MdAutoAwesome,
  MdCalendarMonth,
  MdFileDownload,
} from "react-icons/md";
import { FaWhatsapp } from "react-icons/fa";
import {
  downloadCreditInvoice,
  getCreditOrder,
} from "../../../../api/settings/Credits/credits";
import { MdSms } from "react-icons/md";

/**
 * The "you're all set" screen after a payment succeeds — for a plan renewal
 * and for a credit purchase. A short burst of confetti, the headline figures
 * counted up, and a receipt of exactly what was bought.
 *
 * `order` is the server's order object (credits controller `publicOrder`).
 */

const INK = "#2F3A45";
const MUTED = "#6B7683";
const BRAND = "#0285c7";
const BRAND_DEEP = "#0369a1";
const GREEN = "#2E9A5F";
const LINE = "#E6EBF0";

const CONFETTI = ["#0285c7", "#DE4AC4", "#6AB483", "#F5B942", "#A6D9F0", "#F08A6B"];

let injected = false;
const ensureKeyframes = () => {
  if (injected || typeof document === "undefined") return;
  injected = true;
  const style = document.createElement("style");
  style.textContent = `
@keyframes simpldConfetti{0%{transform:translate3d(0,-20px,0) rotate(0);opacity:0}
  10%{opacity:1}100%{transform:translate3d(var(--dx),320px,0) rotate(var(--rot));opacity:0}}
@keyframes simpldPop{0%{transform:scale(.2);opacity:0}60%{transform:scale(1.12);opacity:1}100%{transform:scale(1)}}
@keyframes simpldRing{0%{transform:scale(.8);opacity:.6}100%{transform:scale(1.9);opacity:0}}
@keyframes simpldRise{0%{transform:translateY(12px);opacity:0}100%{transform:translateY(0);opacity:1}}
@keyframes simpldTwinkle{0%,100%{transform:scale(.8) rotate(0);opacity:.5}50%{transform:scale(1.15) rotate(20deg);opacity:1}}
@media (prefers-reduced-motion:reduce){.simpld-celebrate{animation:none!important}}`;
  document.head.appendChild(style);
};

const useCountUp = (target, ms = 900) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    const end = Number(target) || 0;
    if (!end) {
      setValue(0);
      return undefined;
    }
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min((now - start) / ms, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(Math.round(end * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, ms]);
  return value;
};

const Confetti = () => {
  const pieces = useMemo(
    () =>
      Array.from({ length: 34 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        dx: (Math.random() - 0.5) * 120,
        rot: (Math.random() > 0.5 ? 1 : -1) * (180 + Math.random() * 540),
        delay: Math.random() * 0.5,
        dur: 1.8 + Math.random() * 1.4,
        color: CONFETTI[i % CONFETTI.length],
        w: 6 + Math.random() * 6,
        h: 10 + Math.random() * 8,
        round: i % 4 === 0,
      })),
    []
  );
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 overflow-hidden"
    >
      {pieces.map((p) => (
        <span
          key={p.id}
          className="simpld-celebrate absolute block"
          style={{
            left: `${p.left}%`,
            top: 0,
            width: p.w,
            height: p.round ? p.w : p.h,
            background: p.color,
            borderRadius: p.round ? "50%" : 2,
            opacity: 0,
            "--dx": `${p.dx}px`,
            "--rot": `${p.rot}deg`,
            animation: `simpldConfetti ${p.dur}s ${p.delay}s ease-out forwards`,
          }}
        />
      ))}
    </div>
  );
};

const money = (n) =>
  `₹${(Number(n) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
const count = (n) => (Number(n) || 0).toLocaleString("en-IN");

const longDate = (value) =>
  value
    ? new Date(value).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      })
    : "";

const paidOn = (value) =>
  value
    ? new Date(value).toLocaleString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

const MODE = { upi: "UPI", net_banking: "Net Banking", card: "Card" };

const Counter = ({ value, prefix = "" }) => {
  const shown = useCountUp(value);
  return (
    <>
      {prefix}
      {count(shown)}
    </>
  );
};

const Tile = ({ icon: Icon, color, bg, label, children, sub, wide }) => (
  <div
    className={`flex-1 rounded-xl px-3 py-2 ${wide ? "basis-full" : "min-w-[110px]"}`}
    style={{ background: bg, animation: "simpldRise .5s .35s both" }}
  >
    <div className="flex items-center gap-1 text-[11px]" style={{ color: MUTED }}>
      <Icon size={14} style={{ color }} />
      {label}
    </div>
    <div className="text-[18px] font-bold leading-tight" style={{ color: INK }}>
      {children}
    </div>
    {sub && (
      <div className="text-[10.5px] leading-tight" style={{ color: MUTED }}>
        {sub}
      </div>
    )}
  </div>
);

const Row = ({ label, value, strong }) =>
  value === null || value === undefined || value === "" ? null : (
    <div className="flex justify-between gap-3 py-0.5 text-[12px]">
      <span style={{ color: MUTED }}>{label}</span>
      <span
        className="text-right"
        style={{ color: INK, fontWeight: strong ? 700 : 500 }}
      >
        {value}
      </span>
    </div>
  );

const PaymentSuccess = ({ order: initial, onDone, onMore, moreLabel = "Buy more" }) => {
  ensureKeyframes();
  // The invoice is issued a few seconds after payment, so keep looking until
  // it is ready (about a minute at most) and fill in its number as it appears.
  const [order, setOrder] = useState(initial);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState("");

  useEffect(() => setOrder(initial), [initial]);

  const id = initial?._id;
  const ready = !!order?.invoiceReady;
  // Sample orders in the preview gallery have no server behind them.
  const isSample = String(id || "").startsWith("preview-");
  useEffect(() => {
    if (!id || ready || isSample) return undefined;
    let tries = 0;
    let stopped = false;
    const timer = setInterval(async () => {
      tries += 1;
      if (stopped || tries > 40) {
        clearInterval(timer);
        return;
      }
      try {
        const response = await getCreditOrder(id);
        const latest = response?.data?.data;
        if (latest) {
          setOrder((current) => ({ ...current, ...latest }));
          if (latest.invoiceReady) clearInterval(timer);
        }
      } catch (e) {
        /* keep trying until the limit */
      }
    }, 2000);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [id, ready, isSample]);

  const download = async () => {
    if (!id || downloading || isSample) return;
    setDownloading(true);
    setDownloadError("");
    try {
      const blob = await downloadCreditInvoice(id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Simpld-${order.invoiceNumber || "invoice"}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      setDownloadError("The invoice could not be downloaded. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  if (!order) return null;

  const isPlan = order.kind === "plan";
  const change = order.change;
  const mode = MODE[order.payment?.mode] || order.payment?.mode;

  const messages = (order.items || []).reduce(
    (acc, i) => ({ ...acc, [i.channel]: (acc[i.channel] || 0) + i.messages }),
    {}
  );

  return (
    <div
      className="relative mx-auto flex w-full max-w-[380px] flex-col overflow-hidden rounded-2xl bg-white"
      style={{ maxHeight: "calc(100vh - 150px)" }}
    >
      <div className="min-h-0 flex-1 overflow-y-auto">
        {/* header: tick + headline side by side to save height */}
        <div
          className="relative flex items-center gap-3 px-4 py-3"
          style={{
            background: "linear-gradient(160deg, #E6F6EC 0%, #EAF4FB 100%)",
          }}
        >
          <Confetti />
          <div className="relative flex h-[46px] w-[46px] shrink-0 items-center justify-center">
            <span
              className="simpld-celebrate absolute inset-0 rounded-full"
              style={{
                background: GREEN,
                opacity: 0,
                animation: "simpldRing 1.4s .2s ease-out 2",
              }}
            />
            <span
              className="simpld-celebrate relative flex h-[46px] w-[46px] items-center justify-center rounded-full text-white"
              style={{
                background: `linear-gradient(135deg, ${GREEN} 0%, #4DBB82 100%)`,
                boxShadow: "0 6px 14px rgba(46,154,95,0.3)",
                animation: "simpldPop .6s cubic-bezier(.2,1.4,.4,1) both",
              }}
            >
              <MdCheck size={28} />
            </span>
            <MdAutoAwesome
              className="simpld-celebrate absolute -right-2 -top-1"
              size={14}
              style={{ color: "#F5B942", animation: "simpldTwinkle 1.6s ease-in-out infinite" }}
            />
          </div>
          <div className="relative min-w-0">
            <h2
              className="text-[17px] font-bold leading-tight"
              style={{ color: INK, animation: "simpldRise .5s .15s both" }}
            >
              {isPlan ? "You're all set!" : "Credits added!"}
            </h2>
            <p
              className="text-[12px] leading-snug"
              style={{ color: MUTED, animation: "simpldRise .5s .25s both" }}
            >
              {isPlan
                ? `${order.plan?.name} is active. Thank you for renewing!`
                : "Payment received — messages are ready to go."}
            </p>
          </div>
        </div>

        <div className="px-4 pb-3 pt-3">
          <div className="flex flex-wrap gap-2">
            {isPlan ? (
              <>
                <Tile
                  icon={MdCalendarMonth}
                  color={BRAND}
                  bg="#EAF4FB"
                  label="Valid until"
                  sub={`Term starts ${longDate(order.plan?.startedAt)}`}
                  wide
                >
                  <span style={{ whiteSpace: "nowrap" }}>
                    {longDate(order.plan?.expiresAt)}
                  </span>
                </Tile>
                {order.plan?.whatsapp > 0 && (
                  <Tile icon={FaWhatsapp} color="#25D366" bg="#EAF8F0" label="WhatsApp">
                    <Counter value={order.plan.whatsapp} />
                  </Tile>
                )}
                <Tile icon={MdSms} color={BRAND} bg="#EEF2FB" label="SMS">
                  <Counter value={order.plan?.sms} />
                </Tile>
              </>
            ) : (
              <>
                {messages.whatsapp > 0 && (
                  <Tile
                    icon={FaWhatsapp}
                    color="#25D366"
                    bg="#EAF8F0"
                    label="WhatsApp added"
                    sub={
                      change
                        ? `balance ${count(change.before.whatsapp)} → ${count(change.after.whatsapp)}`
                        : order.balances
                        ? `balance ${count(order.balances.whatsapp)}`
                        : null
                    }
                  >
                    <Counter value={messages.whatsapp} prefix="+" />
                  </Tile>
                )}
                {messages.sms > 0 && (
                  <Tile
                    icon={MdSms}
                    color={BRAND}
                    bg="#EEF2FB"
                    label="SMS added"
                    sub={
                      change
                        ? `balance ${count(change.before.sms)} → ${count(change.after.sms)}`
                        : order.balances
                        ? `balance ${count(order.balances.sms)}`
                        : null
                    }
                  >
                    <Counter value={messages.sms} prefix="+" />
                  </Tile>
                )}
              </>
            )}
          </div>

          <div
            className="mt-3 rounded-xl border px-3 py-2"
            style={{ borderColor: LINE, animation: "simpldRise .5s .5s both" }}
          >
            {order.subTotal !== undefined && order.taxTotal !== undefined && (
              <div
                className="mb-1 flex flex-wrap justify-between gap-x-3 border-b pb-1 text-[11px]"
                style={{ borderColor: LINE, color: MUTED }}
              >
                <span>Amount {money(order.subTotal)}</span>
                <span>
                  GST{order.taxPercentage ? ` ${order.taxPercentage}%` : ""}{" "}
                  {money(order.taxTotal)}
                </span>
                {Math.abs(order.roundOff || 0) >= 0.005 && (
                  <span>
                    Round off {order.roundOff < 0 ? "−" : "+"}
                    {money(Math.abs(order.roundOff))}
                  </span>
                )}
              </div>
            )}
            <Row label="Total paid" value={money(order.total)} strong />
            <Row
              label={mode || "Paid"}
              value={paidOn(order.payment?.paidAt)}
            />
            <Row
              label="Invoice"
              value={order.invoiceNumber || "Being prepared…"}
            />
            {order.payment?.reference && (
              <Row label="Ref" value={order.payment.reference} />
            )}
          </div>
        </div>
      </div>

      {/* Always visible, whatever the window height. */}
      <div
        className="flex shrink-0 flex-wrap items-center justify-center gap-2 border-t px-4 py-2.5"
        style={{ borderColor: LINE, background: "#FFFFFF" }}
      >
        {id && (
          <button
            type="button"
            onClick={download}
            disabled={!ready || downloading}
            className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[12.5px] font-semibold disabled:opacity-60"
            style={{ borderColor: "#BFDDEE", color: BRAND, background: "#F3F9FD" }}
          >
            <MdFileDownload size={16} />
            {downloading ? "Downloading…" : ready ? "Invoice PDF" : "Preparing invoice…"}
          </button>
        )}
        {onMore && (
          <button
            type="button"
            onClick={onMore}
            className="rounded-lg border px-3 py-1.5 text-[12.5px] font-semibold"
            style={{ borderColor: "#BFDDEE", color: BRAND }}
          >
            {moreLabel}
          </button>
        )}
        {onDone && (
          <button
            type="button"
            onClick={onDone}
            className="rounded-lg px-5 py-1.5 text-[12.5px] font-semibold text-white"
            style={{
              background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_DEEP} 100%)`,
            }}
          >
            Done
          </button>
        )}
        {downloadError && (
          <p className="w-full text-center text-[11px]" style={{ color: "#C94B4B" }}>
            {downloadError}
          </p>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
